import React, { useCallback, useMemo, useState } from 'react';
import type { List } from 'immutable';
import { connect } from 'react-redux';
import { PortalModal } from '~/components/views/common/Modal/Modal';
import type { AppState } from '~/reducers';
import type ErrorRecord from '~/data/ErrorRecord';
import PrimaryButton from '~/components/views/common/buttons/PrimaryButton/PrimaryButton';
import { ModalButtons, ModalContent } from '~/components/views/common/Modal';
import useOnMountEffect from '~/hooks/use-on-mount-effect';
import { getLanguageFromLocale } from '~/utils/locale-mapper/locale-mapper';
import {
  toggleYoutubePlaylistModalOpen as toggleYoutubePlaylistModalOpenAction,
  getYoutubePlaylist as getYoutubePlaylistAction,
} from './actions';
import type YoutubeVideoResourceRecord from './data/YoutubeVideoResourceRecord';
import { NoVideoSelected, YoutubePlaylist, YoutubePlayer } from './components/index';
import YoutubePlaylistLoading from './YoutubePlaylistLoading';
import YoutubePlaylistError from './YoutubePlaylistError';
import './YoutubePlaylistContainer.pcss';

interface Props {
  playlistId: string;
}

interface StateProps {
  locale?: string;
  isLoadingPlaylist: boolean;
  videos: List<YoutubeVideoResourceRecord>;
  error?: ErrorRecord;
}

interface DispatchProps {
  toggleYoutubePlaylistModalOpen: typeof toggleYoutubePlaylistModalOpenAction;
  getYoutubePlaylist: typeof getYoutubePlaylistAction;
}

export type ComponentProps = Props & StateProps & DispatchProps;
export const YoutubePlaylistContainer: React.FC<ComponentProps> = ({
  playlistId,
  getYoutubePlaylist,
  locale,
  toggleYoutubePlaylistModalOpen,
  ...props
}) => {
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  const getPlaylist = useCallback(() => {
    getYoutubePlaylist(playlistId);
  }, [playlistId, getYoutubePlaylist]);

  useOnMountEffect(getPlaylist);

  const onCloseClick = useCallback(() => {
    toggleYoutubePlaylistModalOpen();
  }, [toggleYoutubePlaylistModalOpen]);

  const language = useMemo(() => getLanguageFromLocale(locale), [locale]);

  return (
    <PortalModal
      className="YoutubePlaylistContainer"
      close={onCloseClick}
      isOpen={true}
      showCloseIcon={false}
      stickyFooter={true}
      trackingLabel="YoutubePlaylistModal"
      newStyle={true}
    >
      <ModalContent>
        <div className="youtube-player-wrapper">
          {props.error ? (
            <YoutubePlaylistError callback={getPlaylist} />
          ) : props.isLoadingPlaylist ? (
            <YoutubePlaylistLoading />
          ) : activeVideoId !== null ? (
            <YoutubePlayer videoId={activeVideoId} playerLanguage={language} />
          ) : (
            <NoVideoSelected />
          )}
        </div>
        <YoutubePlaylist
          videos={props.videos}
          activeVideoId={activeVideoId}
          onVideoClick={setActiveVideoId}
        />
      </ModalContent>
      <ModalButtons>
        <PrimaryButton langKey="web.youtube_playlist.button.close" onClick={onCloseClick} />
      </ModalButtons>
    </PortalModal>
  );
};

const mapDispatchToProps: DispatchProps = {
  getYoutubePlaylist: getYoutubePlaylistAction,
  toggleYoutubePlaylistModalOpen: toggleYoutubePlaylistModalOpenAction,
};

const mapStateToProps = ({ authentication, youtubePlaylist }: AppState): StateProps => {
  // @ts-expect-error locale moved to translation slice; kept as-is
  const { locale } = authentication;
  const { videos, isLoadingPlaylist, error } = youtubePlaylist;
  return { locale, videos, isLoadingPlaylist, error };
};

export default connect(mapStateToProps, mapDispatchToProps)(YoutubePlaylistContainer);
